import React, { useState } from 'react';
import { Settings2, X, Trash2, Shield, FileText, LogOut, ChevronDown } from 'lucide-react';
import DeleteAccountModal from '../DeleteAccountModal';
import Button from '../../design-system/Button';

const ROLES = [
  { id: 'TOP',     label: 'Top',     emoji: '🛡️' },
  { id: 'JUNGLE',  label: 'Jungle',  emoji: '🌿' },
  { id: 'MID',     label: 'Mid',     emoji: '⚡' },
  { id: 'ADC',     label: 'ADC',     emoji: '🎯' },
  { id: 'SUPPORT', label: 'Support', emoji: '💫' },
];

function RoleSelect({ label, value, onChange, exclude }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold mb-1.5">{label}</div>
      <div className="grid grid-cols-5 gap-1">
        {ROLES.filter(r => r.id !== exclude).map(r => (
          <button key={r.id}
            onClick={() => onChange(value === r.id ? null : r.id)}
            className={`flex flex-col items-center gap-1 py-2 px-1 border text-[9px] font-bold uppercase transition ${
              value === r.id
                ? label.includes('principal')
                  ? 'border-cyan-400 bg-cyan-400/10 text-cyan-400'
                  : 'border-violet-400 bg-violet-400/10 text-violet-400'
                : 'border-zinc-800 hover:border-zinc-600 text-zinc-500'
            }`}
          >
            <span className="text-base">{r.emoji}</span>
            <span>{r.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SettingsPanel({ onClose, onLogout, onDeleted, userEmail, userSettings, onSaveRoles }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [primaryRole, setPrimaryRole]   = useState(userSettings?.primary_role || null);
  const [secondaryRole, setSecondaryRole] = useState(userSettings?.secondary_role || null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  const rolesChanged =
    primaryRole !== (userSettings?.primary_role || null) ||
    secondaryRole !== (userSettings?.secondary_role || null);

  async function handleSaveRoles() {
    if (!primaryRole) return;
    setSaving(true);
    await onSaveRoles?.({ primary_role: primaryRole, secondary_role: secondaryRole });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 z-50 w-80 bg-zinc-950 border-l border-zinc-800 shadow-2xl flex flex-col"
        style={{ animation: 'slideIn 0.2s ease-out' }}>
        <style>{`@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 shrink-0">
          <div className="flex items-center gap-2">
            <Settings2 size={14} className="text-zinc-400" />
            <span className="text-[11px] tracking-[0.3em] uppercase font-bold text-zinc-400">Paramètres</span>
          </div>
          <button onClick={onClose} className="text-zinc-600 hover:text-zinc-300 transition"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">

          {/* Compte */}
          <section>
            <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-600 font-bold mb-3">Compte</div>
            <div className="text-xs text-zinc-500 font-mono mb-4 truncate">{userEmail}</div>
            <button onClick={onLogout}
              className="w-full flex items-center gap-3 text-sm text-zinc-400 hover:text-zinc-200 border border-zinc-800 hover:border-zinc-700 px-4 py-3 transition">
              <LogOut size={14} /> Se déconnecter
            </button>
          </section>

          {/* Rôles */}
          <section className="space-y-4">
            <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-600 font-bold">Rôles</div>
            <RoleSelect
              label="Rôle principal"
              value={primaryRole}
              onChange={setPrimaryRole}
              exclude={secondaryRole}
            />
            <RoleSelect
              label="Rôle secondaire (optionnel)"
              value={secondaryRole}
              onChange={v => setSecondaryRole(v === primaryRole ? null : v)}
              exclude={primaryRole}
            />
            {rolesChanged && primaryRole && (
              <Button variant="primary" className="w-full py-2.5 text-xs" onClick={handleSaveRoles} disabled={saving} loading={saving}>
                {saving ? 'SAUVEGARDE...' : saved ? '✓ SAUVEGARDÉ' : 'SAUVEGARDER LES RÔLES'}
              </Button>
            )}
            {!rolesChanged && saved && (
              <div className="text-xs text-cyan-400 text-center">✓ Rôles mis à jour</div>
            )}
          </section>

          {/* Légal */}
          <section>
            <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-600 font-bold mb-3">Légal</div>
            <div className="space-y-1">
              <a href="#/privacy" onClick={onClose}
                className="w-full flex items-center gap-3 text-sm text-zinc-400 hover:text-zinc-200 border border-zinc-800 hover:border-zinc-700 px-4 py-3 transition">
                <Shield size={14} /> Politique de confidentialité
              </a>
              <a href="#/terms" onClick={onClose}
                className="w-full flex items-center gap-3 text-sm text-zinc-400 hover:text-zinc-200 border border-zinc-800 hover:border-zinc-700 px-4 py-3 transition">
                <FileText size={14} /> Conditions d'utilisation
              </a>
            </div>
          </section>

          {/* Zone dangereuse */}
          <section>
            <div className="text-[9px] uppercase tracking-[0.3em] text-rose-800 font-bold mb-3">Zone dangereuse</div>
            <button onClick={() => setShowDeleteModal(true)}
              className="w-full flex items-center gap-3 text-sm text-rose-400 hover:text-rose-300 border border-rose-500/20 hover:border-rose-500/40 bg-rose-500/5 hover:bg-rose-500/10 px-4 py-3 transition">
              <Trash2 size={14} /> Supprimer mon compte
            </button>
            <p className="text-[10px] text-zinc-700 mt-2 leading-relaxed">
              Supprime définitivement ton compte et toutes tes données. Action irréversible.
            </p>
          </section>
        </div>

        <div className="border-t border-zinc-900 px-5 py-3 text-[9px] text-zinc-700 tracking-widest uppercase shrink-0">
          APEX Climber · Not affiliated with Riot Games
        </div>
      </div>

      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => { setShowDeleteModal(false); onDeleted?.(); }}
        />
      )}
    </>
  );
}
